import { createContext, ReactNode, useEffect } from "react";
import { getToken, removeToken } from "../helpers/tokenHelpers";
import { useAppApiClient } from "../hooks/useAppApiClient";
import { useContainer } from "../hooks/useContainer";
import { useAction } from "../libraries/use-action";
import { CurrentUserResponse } from "../services/api/createAppApiClient";
import { useChatSession } from "@chainlit/react-client";
import { useDispatch } from "react-redux";
import { updateScaitCredits, updateScaitPricing } from "../store/store";

export type AuthContextValue = {
  logOut: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSetupFinished: boolean;
  isPaymentFinished: boolean;
  currentUser?: CurrentUserResponse;
};

export const AuthContext = createContext<AuthContextValue>({
  logOut: () => {},
  isLoading: true,
  isAuthenticated: false,
  isSetupFinished: false,
  isPaymentFinished: false,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const tokenFromStorage = getToken();
  const api = useAppApiClient();
  const { config } = useContainer();
  const { disconnect } = useChatSession();
  const dispatch = useDispatch();

  const getCurrentUser = useAction(async () => {
    if (!tokenFromStorage) return;

    const result = await api.getCurrentUser();
    if (!result) {
      removeToken();
      return undefined;
    }

    // SCAIT in maintenance: do not call any SCAIT API
    if (!config.scaitMaintenance) {
      try {
        const [credits, pricing] = await Promise.all([
          api.getCredits(),
          api.getCreditPricing(),
        ]);
        dispatch(updateScaitCredits(credits));
        dispatch(updateScaitPricing(pricing));
      } catch (e) {
        console.warn("Failed to fetch SCAIT credits/pricing", e);
      }
    }

    return result;
  });

  const currentUser = getCurrentUser.result;
  const setCurrentUser = getCurrentUser.resolve;

  const isLoading = getCurrentUser.running;
  const isAuthenticated = !!tokenFromStorage;
  const isSetupFinished =
    isAuthenticated &&
    !!currentUser &&
    currentUser.FirstName !== "N/A" &&
    currentUser.CompanyId !== -99;
  const isPaymentFinished =
    isAuthenticated && !!currentUser && !currentUser.BlockingFlag;

  const logOut = () => {
    removeToken();
    setCurrentUser(undefined);
    disconnect();
    dispatch(updateScaitCredits(null));
    dispatch(updateScaitPricing(null));
  };

  useEffect(
    () => {
      getCurrentUser.run();
    },
    // eslint-disable-next-line
    [tokenFromStorage],
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        currentUser,
        isLoading,
        isSetupFinished,
        isPaymentFinished,
        logOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
